import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.company || !formData.phone || !formData.interest) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('phone', formData.phone);
      formBody.append('interest', formData.interest);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5ubcrskjhtioh5jrldg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          phone: '',
          interest: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <p className="text-teal-600 text-xs md:text-sm font-semibold uppercase tracking-wider mb-2 md:mb-3">
            Contact Us
          </p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            문의하기
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-2">
            <span className="hidden md:inline">전문가와 상담하고 귀사에 최적화된 솔루션을 제안받으세요</span>
            <span className="md:hidden">전문가 상담으로 최적의 솔루션을 제안받으세요</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-start">
          <div className="order-2 lg:order-1">
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl md:rounded-2xl p-6 md:p-10 text-white mb-6 md:mb-8">
              <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">상담 프로세스</h3>
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-base md:text-xl font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg font-bold mb-0.5 md:mb-1">상담 신청</h4>
                    <p className="text-white/90 text-sm md:text-base">온라인 문의 양식을 통해 상담을 신청합니다</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-base md:text-xl font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg font-bold mb-0.5 md:mb-1">전문가 진단</h4>
                    <p className="text-white/90 text-sm md:text-base">전문 컨설턴트가 귀사의 현황을 분석합니다</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-base md:text-xl font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg font-bold mb-0.5 md:mb-1">맞춤 솔루션 제안</h4>
                    <p className="text-white/90 text-sm md:text-base">귀사에 최적화된 솔루션을 제안합니다</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-3 md:gap-6">
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl md:text-2xl text-teal-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base md:text-lg font-bold text-gray-900">전화 문의</h4>
                    <a href="tel:070-4113-1039" className="text-teal-600 font-semibold cursor-pointer text-sm md:text-base">
                      070-4113-1039 / 010-3240-1039
                    </a>
                    <span className="text-gray-500 text-xs md:text-sm ml-2">(평일 09-18시)</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl md:text-2xl text-teal-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base md:text-lg font-bold text-gray-900">이메일 문의</h4>
                    <a href="mailto:s9526019@naver.com" className="text-teal-600 font-semibold cursor-pointer text-sm md:text-base break-all">
                      s9526019@naver.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl md:text-2xl text-teal-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base md:text-lg font-bold text-gray-900">오시는 길</h4>
                    <p className="text-gray-600 text-sm md:text-base">서울특별시 강남구 테헤란로 123</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-8 shadow-lg border border-gray-200 order-1 lg:order-2">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">실시간 상담 신청</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                    성함 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="홍길동"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                    회사명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="(주)유엔아이파트너스"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                  연락처 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="interest" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                  관심분야 <span className="text-red-500">*</span>
                </label>
                <select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">선택해주세요</option>
                  <option value="ISO 9001">ISO 9001 (품질)</option>
                  <option value="ISO 14001">ISO 14001 (환경)</option>
                  <option value="ISO 45001">ISO 45001 (안전보건)</option>
                  <option value="ISO 27001">ISO 27001 (정보보안)</option>
                  <option value="ISO 13485">ISO 13485 (의료기기)</option>
                  <option value="ISO 교육">ISO 교육 (심사원 과정)</option>
                  <option value="이노비즈/메인비즈">이노비즈/메인비즈</option>
                  <option value="벤처기업인증">벤처기업인증</option>
                  <option value="녹색인증">녹색인증</option>
                  <option value="NET/NEP">NET/NEP</option>
                  <option value="소부장 인증">소부장 인증</option>
                  <option value="뿌리기업 확인">뿌리기업 확인</option>
                  <option value="우수조달">우수조달</option>
                  <option value="경영 컨설팅">경영 컨설팅</option>
                  <option value="R&D 과제">R&D 과제</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                  문의내용
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="문의하실 내용을 입력해주세요 (최대 500자)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500자</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
                  <p className="text-green-700 text-xs md:text-sm font-semibold">
                    문의가 성공적으로 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
                  <p className="text-red-700 text-xs md:text-sm font-semibold">
                    문의 접수 중 오류가 발생했습니다. 다시 시도해주세요.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3.5 md:px-8 md:py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm md:text-base"
              >
                {isSubmitting ? '전송 중...' : '상담 신청하기'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
